import React, { useState, useEffect, useRef } from 'react';
import { Send, Search, MessageSquare, ChevronLeft, ChevronRight, Bot } from 'lucide-react';
import chatService from '../../services/chatService';

const AdminChatPage = () => {
    const [rooms, setRooms] = useState([]);
    const [activeRoom, setActiveRoom] = useState(null);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;
    const [jumpPage, setJumpPage] = useState('');

    const messagesEndRef = useRef(null);
    const activeRoomRef = useRef(activeRoom);
    const textareaRef = useRef(null);

    useEffect(() => {
        activeRoomRef.current = activeRoom;
    }, [activeRoom]);

    useEffect(() => {
        fetchRooms();
        
        let isMounted = true; 
        let unsubscribeNotif = null;

        chatService.connect(() => {
            if (isMounted) {
                unsubscribeNotif = chatService.subscribeToStaffNotifications((message) => {
                    if (message.senderType !== 'STAFF') {
                        fetchRooms();
                    }
                });
            }
        });

        return () => {
            isMounted = false; 
            if (unsubscribeNotif) unsubscribeNotif();
            chatService.unsubscribeFromRoom();
        };
    }, []);

    const fetchRooms = async () => {
        try {
            const res = await chatService.getActiveRooms();
            let roomList = Array.isArray(res) ? res : (res?.data || []);
            
            if (activeRoomRef.current) {
                roomList = roomList.map(r => 
                    r.roomId === activeRoomRef.current.roomId ? { ...r, unreadCount: 0 } : r
                );
            }
            
            setRooms(roomList);
        } catch (error) {
            console.error("Lỗi lấy danh sách phòng:", error);
        }
    };

    const handleSelectRoom = async (room) => {
        setActiveRoom(room);
        
        try {
            await chatService.markRoomAsRead(room.roomId);
            window.dispatchEvent(new CustomEvent('chatRoomRead'));
            setRooms(prev => prev.map(r => r.roomId === room.roomId ? { ...r, unreadCount: 0 } : r));

            const res = await chatService.getRoomHistoryForStaff(room.roomId);
            const history = Array.isArray(res) ? res : (res?.data || []);
            setMessages(history);

            chatService.subscribeToRoom(room.roomId, (newMessage) => {
                setMessages(prev => {
                    const exists = prev.find(m => m.messageId === newMessage.messageId);
                    if (exists) return prev;
                    return [...prev, newMessage];
                });
                
                if (newMessage.senderType !== 'STAFF' && newMessage.senderType !== 'ADMIN') {
                    chatService.markRoomAsRead(room.roomId);
                    window.dispatchEvent(new CustomEvent('chatRoomRead'));

                    setRooms(prevRooms => prevRooms.map(r => 
                        r.roomId === room.roomId 
                            ? { ...r, unreadCount: 0, lastMessage: newMessage.content, updatedAt: newMessage.timestamp } 
                            : r
                    ));
                }
            });
        } catch (error) {
            console.error("Lỗi chi tiết phòng:", error);
        }
    };

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSendMessage = (e) => {
        if (e) e.preventDefault();
        if (!inputValue.trim() || !activeRoom) return;

        const messageDto = {
            roomId: activeRoom.roomId,
            senderType: 'STAFF',
            senderName: 'CSKH Thành Nam PC',
            content: inputValue.trim()
        };

        chatService.sendMessage(messageDto);
        setInputValue('');

        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }

        setRooms(prevRooms => prevRooms.map(r => 
            r.roomId === activeRoom.roomId 
                ? { ...r, lastMessage: messageDto.content, updatedAt: new Date().toISOString() } 
                : r
        ));
    };

    const handleInputChange = (e) => {
        setInputValue(e.target.value);
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const filteredRooms = rooms.filter(r => 
        (r.customerName || '').toLowerCase().includes(searchQuery.toLowerCase())
    ).sort((a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime());

    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery]);

    const totalPages = Math.ceil(filteredRooms.length / itemsPerPage) || 1;
    const validCurrentPage = Math.min(currentPage, totalPages);
    const paginatedRooms = filteredRooms.slice((validCurrentPage - 1) * itemsPerPage, validCurrentPage * itemsPerPage);

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleJumpPage = () => {
        const page = parseInt(jumpPage, 10);
        if (!isNaN(page) && page >= 1 && page <= totalPages) {
            handlePageChange(page);
            setJumpPage('');
        } else {
            setJumpPage('');
        }
    };

    // 👉 ĐÃ SỬA GỌN LẠI HOÀN TOÀN LOGIC THỜI GIAN
    const formatDividerTime = (timeStr) => {
        if (!timeStr) return '';
        return new Date(timeStr).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
    };

    const formatTooltipTime = (timeStr) => {
        if (!timeStr) return '';
        const d = new Date(timeStr);
        return `${d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}, ${d.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;
    };

    const formatSidebarTime = (timeStr) => {
        if (!timeStr) return '';
        return new Date(timeStr).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <>
            <style>
                {`
                .admin-chat-textarea::-webkit-scrollbar { width: 4px; }
                .admin-chat-textarea::-webkit-scrollbar-track { background: transparent; }
                .admin-chat-textarea::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
                .admin-chat-textarea::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
                .admin-message-container { overflow-x: visible !important; }
                `}
            </style>
            <div className="flex h-[calc(100vh-64px)] bg-gray-50 overflow-hidden">
                <div className="w-80 bg-white border-r border-gray-200 flex flex-col shrink-0 relative z-20">
                    <div className="p-4 border-b border-gray-100">
                        <h2 className="text-xl font-bold text-gray-800 mb-4">Tin nhắn</h2>
                        <div className="relative">
                            <input 
                                type="text" 
                                placeholder="Tìm kiếm khách hàng..." 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 bg-gray-100 border-transparent rounded-lg text-sm focus:bg-white focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none transition-all"
                            />
                            <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        {filteredRooms.length === 0 ? (
                            <div className="p-6 text-center text-gray-400 text-sm">Không có cuộc trò chuyện nào.</div>
                        ) : (
                            paginatedRooms.map(room => (
                                <button 
                                    key={room.roomId}
                                    onClick={() => handleSelectRoom(room)}
                                    className={`w-full text-left p-4 border-b border-gray-50 hover:bg-gray-50 transition-colors flex items-start gap-3 relative ${activeRoom?.roomId === room.roomId ? 'bg-blue-50 hover:bg-blue-50' : ''}`}
                                >
                                    <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center shrink-0 font-bold">
                                        {room.customerName?.charAt(0).toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between items-baseline mb-1">
                                            <h4 className={`text-sm truncate pr-2 ${room.unreadCount > 0 ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>
                                                {room.customerName} 
                                                {room.username && <span className="text-gray-400 font-normal text-xs ml-1">@{room.username}</span>}
                                            </h4>
                                            <span className="text-[10px] text-gray-400 shrink-0">{formatSidebarTime(room.updatedAt)}</span>
                                        </div>
                                        
                                        <div className={`text-[12px] truncate mb-1.5 ${room.unreadCount > 0 ? 'text-gray-800 font-medium' : 'text-gray-500'}`}>
                                            {room.lastMessage || "Chưa có tin nhắn..."}
                                        </div>

                                        <div className="flex justify-between items-center">
                                            <span className="text-[10px] text-gray-400 truncate">
                                                {room.account ? <span className="text-green-600 font-medium">Thành viên</span> : 'Khách vãng lai'}
                                            </span>
                                            {room.unreadCount > 0 && (
                                                <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0">
                                                    {room.unreadCount}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    {activeRoom?.roomId === room.roomId && (
                                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500 rounded-r"></div>
                                    )}
                                </button>
                            ))
                        )}
                    </div>

                    {totalPages > 1 && (
                        <div className="p-3 bg-white border-t border-gray-100 flex flex-col gap-2 shrink-0 shadow-[0_-2px_10px_rgba(0,0,0,0.02)]">
                            <div className="flex justify-center items-center gap-1">
                                <button 
                                    onClick={() => handlePageChange(validCurrentPage - 1)} 
                                    disabled={validCurrentPage === 1}
                                    className="p-1.5 rounded bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                                >
                                    <ChevronLeft size={16} />
                                </button>
                                <span className="text-xs font-bold text-gray-600 px-2">
                                    {validCurrentPage} / {totalPages}
                                </span>
                                <button 
                                    onClick={() => handlePageChange(validCurrentPage + 1)} 
                                    disabled={validCurrentPage === totalPages}
                                    className="p-1.5 rounded bg-white text-gray-600 border border-gray-200 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                                >
                                    <ChevronRight size={16} />
                                </button>
                            </div>
                            <div className="flex items-center justify-center gap-2">
                                <span className="text-[11px] text-gray-500 font-medium">Đến:</span>
                                <input
                                    type="number"
                                    min="1"
                                    max={totalPages}
                                    value={jumpPage}
                                    onChange={(e) => setJumpPage(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleJumpPage()}
                                    placeholder="Trang"
                                    className="w-12 h-6 px-1 text-center text-xs border border-gray-200 rounded outline-none focus:border-tnc-orange"
                                />
                                <button
                                    onClick={handleJumpPage}
                                    className="px-2 h-6 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs rounded font-bold transition-colors"
                                >
                                    Đi
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex-1 flex flex-col bg-[#f0f2f5] relative z-10">
                    {!activeRoom ? (
                        <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
                            <MessageSquare size={64} className="mb-4 opacity-20" />
                            <p>Chọn một cuộc trò chuyện để bắt đầu trả lời.</p>
                        </div>
                    ) : (
                        <>
                            <div className="h-16 bg-white border-b border-gray-200 px-6 flex items-center justify-between shrink-0 shadow-sm z-20">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-bold">
                                        {activeRoom.customerName?.charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-gray-800">{activeRoom.customerName}</h3>
                                        <div className="flex items-center gap-1 text-xs text-green-600 font-medium">
                                            <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                            Phòng Chat #{activeRoom.roomId}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="admin-message-container flex-1 overflow-y-auto p-6 flex flex-col gap-4 overflow-x-hidden">
                                {messages.map((msg, idx) => {
                                    const isOwnMessage = ['STAFF', 'ADMIN', 'BOT', 'SYSTEM'].includes(msg.senderType);
                                    const isBot = ['BOT', 'SYSTEM'].includes(msg.senderType);

                                    const prevMsg = idx > 0 ? messages[idx - 1] : null;
                                    let showTimeDivider = false;
                                    
                                    if (!prevMsg) {
                                        showTimeDivider = true;
                                    } else {
                                        const currTime = new Date(msg.timestamp).getTime();
                                        const prevTime = new Date(prevMsg.timestamp).getTime();
                                        if (currTime - prevTime > 1800000) showTimeDivider = true;
                                    }

                                    return (
                                        <React.Fragment key={msg.messageId || idx}>
                                            {showTimeDivider && (
                                                <div className="w-full flex justify-center my-2">
                                                    <span className="text-[11px] font-medium text-gray-400 bg-gray-200/50 px-3 py-1 rounded-full">
                                                        {formatDividerTime(msg.timestamp)}
                                                    </span>
                                                </div>
                                            )}

                                            <div className={`flex w-full ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
                                                <div className="relative group/bubble max-w-[70%]">
                                                    <div className={`px-5 py-2.5 rounded-2xl text-sm shadow-sm relative z-10 ${
                                                        isOwnMessage 
                                                        ? (isBot ? 'bg-blue-600 text-white rounded-tr-sm' : 'bg-tnc-orange text-white rounded-tr-sm')
                                                        : 'bg-white text-gray-800 border border-gray-100 rounded-tl-sm'
                                                    }`}>
                                                        {isBot && (
                                                            <div className="flex items-center gap-1 text-[10px] font-bold text-blue-100 mb-1 border-b border-blue-400/50 pb-1">
                                                                <Bot size={12} /> Hệ thống tự động
                                                            </div>
                                                        )}
                                                        <p className="leading-relaxed whitespace-pre-wrap break-words">{msg.content}</p>
                                                    </div>

                                                    <div className={`absolute top-1/2 -translate-y-1/2 opacity-0 group-hover/bubble:opacity-100 transition-opacity duration-200 text-[11px] font-medium text-gray-500 bg-white/90 backdrop-blur-sm px-2.5 py-1.5 rounded-md border border-gray-200 whitespace-nowrap pointer-events-none z-0 shadow-sm ${
                                                        isOwnMessage ? 'right-[102%] mr-2' : 'left-[102%] ml-2'
                                                    }`}>
                                                        {formatTooltipTime(msg.timestamp)}
                                                    </div>
                                                </div>
                                            </div>
                                        </React.Fragment>
                                    );
                                })}
                                <div ref={messagesEndRef} />
                            </div>

                            <div className="p-4 bg-white border-t border-gray-200 shrink-0">
                                <form onSubmit={handleSendMessage} className="flex items-end gap-3 max-w-4xl mx-auto">
                                    <textarea 
                                        ref={textareaRef}
                                        value={inputValue}
                                        onChange={handleInputChange}
                                        onKeyDown={handleKeyDown}
                                        rows={1}
                                        placeholder="Nhập câu trả lời của bạn (Shift + Enter để xuống dòng)..."
                                        className="admin-chat-textarea flex-1 bg-gray-100 border-transparent focus:bg-white focus:border-tnc-orange focus:ring-1 focus:ring-orange-200 rounded-xl px-5 py-3 outline-none transition-all resize-none overflow-y-auto min-h-[48px] max-h-[120px] leading-relaxed text-[14px]"
                                    />
                                    <button 
                                        type="submit" 
                                        disabled={!inputValue.trim()}
                                        className="h-12 bg-tnc-orange text-white rounded-xl px-6 font-bold flex items-center justify-center hover:bg-tnc-orange-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0 gap-2 shadow-sm mb-0.5"
                                    >
                                        Gửi <Send size={18} />
                                    </button>
                                </form>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default AdminChatPage;